import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import Popup from 'components/molecules/popup';
import { FooterTable, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useGetUnpaidAdvances,
  useGetUnpaidAdvancesStatistics,
} from 'services/advance';
import { useGetLabelLightList } from 'services/labels/light';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrency } from 'utils/global/currency.utils';
import { getMaxFormattedLength } from 'utils/global/getMaxFormattedLength.utils';
import { object, string } from 'yup';

enum COLUMNS {
  number,
  label,
  amount,
  remainingNetAmount,
}

const Advance = () => {
  usePermissionGuard('AdvancePermissionsLIST**', '/', true, 'statements');

  const { t } = useTranslation();
  const navigate = useNavigate();

  const columnsVisibility = useTableColumnsVisibility('Advance', COLUMNS);

  const advances = useGetUnpaidAdvances();
  const statistics = useGetUnpaidAdvancesStatistics();

  const data = {
    nodes:
      advances.data?.pages.flat().map(data => ({
        ...data,
        id: `/label/${data.labelId}/advance/${data.id}/repayments`,
      })) ?? [],
  };

  const onSelectChange = (action: Action, state: State) => {
    const id: string = state.id ?? state.ids[state.ids.length - 1];
    navigate(id, { state: true });
  };

  const select = useRowSelect(data, {
    onChange: onSelectChange,
  });

  const allAmountLength = formatCurrency(statistics?.data?.allNetAmount).length;

  const amountLength = getMaxFormattedLength(data?.nodes ?? [], e => e?.amount);

  const labelsLightLits = useGetLabelLightList();

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`unpaid.advance`)}
          path=''
          hideBackButton
          topbarContent={
            <div className='flex gap-2'>
              <Table.CustomColumnsButton
                columns={COLUMNS}
                isClear
                visibility={columnsVisibility}
                customI18next=''
                i18nextPrefix={'input.label'}
              />
              <Popup.Form
                header={t('advance.create-advance')}
                initialValues={{ labelId: '' }}
                validationSchema={object().shape({
                  labelId: string().required(() => t('formik-error.required')),
                })}
                trigger={
                  <Button variant={BUTTON_VARIANT.submit}>
                    <Button.Text text={t('button.create')} />
                  </Button>
                }
                submitButton={
                  <Button type='submit' variant={BUTTON_VARIANT.submit}>
                    <Button.Text text={t('button.go-to-form')} />
                  </Button>
                }
                onSubmit={async values =>
                  navigate(`/label/${values.labelId}/advance/create`)
                }>
                <FormInput.Select
                  name='labelId'
                  label={t('input.label.label')}
                  options={labelsLightLits.data?.map(label => ({
                    value: label.id,
                    label: label.labelName,
                  }))}
                />
              </Popup.Form>
            </div>
          }
        />
      }>
      <FooterTable
        id={columnsVisibility.tableId}
        loading={
          advances.isLoading ||
          advances.isFetchingNextPage ||
          statistics.isLoading
        }
        select={select}
        onFullyScroll={advances.fetchNextPage}
        data={data}
        displayMode
        columns={[
          {
            label: t('input.label.number'),
            renderCell: ({ advanceProposalNumber }) => (
              <TableItem>
                <TableItem.Text text={advanceProposalNumber} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.number),
            footer: t('table.total'),
          },
          {
            label: t('input.label.label'),
            renderCell: ({ labelName }) => (
              <TableItem>
                <TableItem.Text text={labelName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.label),
          },
          {
            label: t('input.label.amount'),
            renderCell: ({ amount }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amount, {
                    length: amountLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.amount),
          },
          {
            label: t('input.label.remainingNetAmount'),
            renderCell: ({ remainingNetAmount }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(remainingNetAmount, {
                    length: allAmountLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.remainingNetAmount),
            footer: formatCurrency(statistics.data?.allNetAmount),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default Advance;
